// A live nav-pod example, used by both nav-pod surfaces: pinned at the top of the Style pivot as a
// hardcoded sample, and inside each expanded row of the Pods list as that pod's own preview.
//
// It renders the REAL `.storefront-nav-pod-*` markup with the config's style dicts applied inline
// through the shared builders, so "what you see" and "what the canvas draws" come from one source.
// The classes are plain global rules (no parent scope), so importing the sheet is all this needs to
// paint correctly inside a Fluent Panel, which renders in a Layer outside the editor's DOM.
//
// The sample pod is self-contained (an inline SVG authored below, no fetch, no tenant data) so the
// example paints instantly and identically for every storefront and can't leak one storefront's
// content into another's styling surface.

import React from 'react';
import { ISearchElementStyle, ISearchTextStyle } from '../searchPageConfig';
import { elementStyleFrom, textStyleFrom } from '../styleControls';
import StyleExampleFrame from '../StyleExampleFrame';
import '../StorefrontEditor.css';

// An iconSvg is a recolorable shape, not a picture: the storefront tints it by masking, so only the
// outline matters and the SVG's own colors are ignored. Masking also sizes any SVG correctly
// (mask-size: contain) regardless of the markup's intrinsic width/height.
export const svgMaskStyle = (svg: string, size: number): React.CSSProperties => {
    const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
    return {
        width: size,
        height: size,
        flex: '0 0 auto',
        backgroundColor: 'currentColor',
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
    };
};

// A laptop silhouette: two filled shapes and nothing else. A mask only reads the alpha channel, so
// outlines and interior detail would disappear anyway, and a solid silhouette stays legible at the
// 64px the pods draw it at. encodeURIComponent (not base64) so the source stays readable in place.
const SAMPLE_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <rect x="10" y="12" width="44" height="30" rx="4"/>
  <rect x="4" y="46" width="56" height="6" rx="3"/>
</svg>`;

/** The content slots a pod fills. Everything else about the pod is style. */
export interface INavPodExampleContent {
    name?: string;
    linkText?: string;
    imageUrl?: string;
    iconSvg?: string;
}

const SAMPLE_POD: INavPodExampleContent = {
    name: 'Laptops & Tablets',
    linkText: 'Shop now ›',
    iconSvg: SAMPLE_ICON_SVG,
};

export interface INavPodExampleProps {
    /** Raw config dicts, resolved here through the same builders the canvas uses. */
    navPod?: ISearchElementStyle;
    navPodName?: ISearchTextStyle;
    navPodImage?: ISearchElementStyle;
    navPodLink?: ISearchTextStyle;
    /** Tint for a masked SVG icon, and its hover recolor. Unset leaves the icon on currentColor. */
    iconColor?: string;
    iconHoverColor?: string;
    /**
     * The resolved hover part, merged over the pod while the pointer is inside the frame. Resolved
     * by the caller (resolveHoverPart) so this component stays a dumb renderer.
     */
    hoverStyle?: React.CSSProperties;
    isDark?: boolean;
    /**
     * A real pod's content, replacing the built-in sample. Set by the management list, where the
     * preview has to show the pod being edited; the style surfaces leave it unset.
     */
    pod?: INavPodExampleContent;
    caption?: string;
}

const NavPodExample: React.FC<INavPodExampleProps> = (props) => {
    const [hovered, setHovered] = React.useState(false);

    const content = props.pod ?? SAMPLE_POD;
    // The live store omits an unset title; a real pod being edited shows a dimmed placeholder
    // instead, so an unfinished pod is still identifiable in the swatch.
    const untitled = !!props.pod && !props.pod.name;

    // The hover part is a `:hover` rule on the live storefront (HOVER_PARTS in stylesConfig); here
    // it's simulated by merging it over the pod while the pointer is inside the frame, so the
    // merchant can see what they're editing without a real page to hover.
    const hasHover = !!props.hoverStyle && Object.keys(props.hoverStyle).length > 0;
    const podStyle: React.CSSProperties = {
        ...elementStyleFrom(props.navPod),
        ...((hasHover && hovered) ? props.hoverStyle : {}),
    };
    const imageStyle = elementStyleFrom(props.navPodImage);
    // Icon color wins over anything the image dict sets, because for a masked span the background
    // color IS the tint and the dedicated control is the one the merchant just used.
    const iconTint = (hovered && props.iconHoverColor) ? props.iconHoverColor : props.iconColor;

    const caption = props.caption && hasHover
        ? `${props.caption} · point at the pod to preview hover styling`
        : props.caption;

    return (
        <StyleExampleFrame isDark={props.isDark} caption={caption} onHoverChange={setHovered}>
            <div
                className={`storefront-nav-pod-button${props.isDark ? ' storefront-nav-pod-button-dark' : ''}`}
                style={podStyle}
            >
                {/* Same precedence as the live store: an SVG icon wins over an uploaded image. */}
                {content.iconSvg
                    ? <span
                        className='storefront-nav-pod-image storefront-nav-pod-icon'
                        style={{ ...svgMaskStyle(content.iconSvg, 64), ...imageStyle, ...(iconTint ? { backgroundColor: iconTint } : {}) }}
                    />
                    : content.imageUrl
                        // No dark-background invert: the storefront's dark theme inverts category-pod
                        // art only, never nav-pod art. Dropped here as well as in the canvas preview so
                        // this example and the page it previews can't disagree.
                        ? <img
                            className='storefront-nav-pod-image'
                            src={content.imageUrl}
                            alt={content.name ?? ''}
                            style={imageStyle}
                        />
                        : null}
                <div
                    className='storefront-nav-pod-name'
                    style={{ ...textStyleFrom(props.navPodName), ...(untitled ? { opacity: 0.5, fontStyle: 'italic' } : {}) }}
                >
                    {content.name || 'Untitled pod'}
                </div>
                {content.linkText && (
                    <div className='storefront-nav-pod-link' style={textStyleFrom(props.navPodLink)}>{content.linkText}</div>
                )}
            </div>
        </StyleExampleFrame>
    );
};

export default NavPodExample;
